import { DatePicker, Form, Input, Modal, Select, Switch } from "antd";
import TextArea from "antd/es/input/TextArea";
import { AppButton } from "components/button/AppButton";
import dayjs from "dayjs";
import { useGetSectors } from "features/jobs/hooks/useGetSectors";
import { useCreateProject } from "features/settings/hooks/useCreateProject";
import { QUERY_KEY_FOR_TALENT_PROFILE } from "features/settings/hooks/useGetTalentProfile";
import { ICreateProject } from "features/settings/types";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { useState } from "react";
import { useQueryClient } from "react-query";
import { IdentifierProps } from "types";
import {
  generalValidationRules,
  textInputValidationRules,
} from "utils/formHelpers/validations";
import { openNotification } from "utils/notification";

export const AddProject = ({ open, handleClose }: IdentifierProps) => {
  const [currentWork, setCurrentWork] = useState(false);
  const [form] = Form.useForm();
  const { token, userInfo } = useGetUserInfo();
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useCreateProject();
  const { data: sectorData, isLoading: loadingSector } = useGetSectors();

  const disableFutureDates = (current: dayjs.Dayjs) => {
    return current && current.isAfter(dayjs().endOf('day'));
  };

  const handleSubmit = (data: any) => {
    const props: ICreateProject = {
      talent_id: userInfo?.talent?.id,
      title: data.title,
      description: data.description,
      type: data.type,
      link: data.link,
      date: data.date,
      role: data.role,
      token,
    };
    mutate(props, {
      onError: (err: any) => {
        let errorTitle = "Error";
        let errorDescription = "An error occurred, please try again.";

        if (err.response && err.response.data && err.response.data.error) {
          errorTitle = "Error: " + err.response.data.error;
          errorDescription = err.response.data.description || "Please try again.";
        }

        openNotification({
          title: errorTitle,
          state: "error",
          description: errorDescription,
          duration: 6,
        });
      },
      onSuccess: (res: any) => {
        openNotification({
          title: "Success",
          state: "success",
          description: "Project created successfully",
          duration: 4.5,
        });
        form.resetFields();
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_FOR_TALENT_PROFILE],
        });
        handleClose();
      },
    });
  };

  return (
    <Modal
      title="Add Project"
      open={open}
      onCancel={() => handleClose(false)}
      footer={null}
      width="45rem"
      style={{ top: 10 }}
    >
      <div>
        <Form
          layout="vertical"
          requiredMark={false}
          onFinish={handleSubmit}
          form={form}
          initialValues={{ is_current: false }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5">
            <Form.Item
              name="title"
              label="Title"
              rules={generalValidationRules}
            >
              <Input placeholder="Enter Title" />
            </Form.Item>


            <Form.Item
              name="type"
              label="Project Type"
              rules={[{ required: true, message: "Please select a project type!" }]}
            >
              <Select showSearch placeholder="Select Project Type">
                <Select.Option value="personal">Personal</Select.Option>
                <Select.Option value="freelance">Freelance</Select.Option>
                <Select.Option value="academic">Academic</Select.Option>
                <Select.Option value="volunteer">Volunteer</Select.Option>
                <Select.Option value="community">Community Projects</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="link"
              label="Project Link"
              rules={generalValidationRules}
            >
              <Input placeholder="Enter Project Link" />
            </Form.Item>


            <Form.Item
              name="date"
              label="Project Date"
              rules={[{ required: true, message: "Please select a project date!" }]}
            >
              <DatePicker style={{ width: "100%" }} disabledDate={disableFutureDates} />
            </Form.Item>


            <Form.Item
              name="role"
              label="Role/Contribution"
              rules={[{ required: true, message: "Please enter your role!" }]}
            >

              <Select showSearch placeholder="Select Project Type">
                <Select.Option value="Owner">Owner</Select.Option>
                <Select.Option value="Contributor">Contributor</Select.Option>
              </Select>

            </Form.Item>
          </div>

          <Form.Item
            className="col-span-2"
            name="description"
            label="Description"
          >
            <TextArea required />
          </Form.Item>
          <div className="flex">
            <AppButton type="submit" isLoading={isLoading} />
          </div>
        </Form>
      </div>
    </Modal>
  );
};
